<template>
  <div class="modal overflow-auto" select-cy="update-supporter-details">
    <div class="card inner">
      <div class="header">
        <div class="title">
          <h2>Add Address</h2>
        </div>
        <div class="close" @click="close()">
          <i class="icn icn-x sm"></i>
        </div>
      </div>
      <div class="content">
        <form id="address-add" @submit.prevent @submit="add">
          <h3 class="sub-title">Details</h3>
          <div class="row mt-2" v-if="labels">
            <div class="field">
              <select required v-model="item.label">
                <option :value="l" :selected="l === item.label" v-for="l of labels" :key="l">
                  {{ l }}
                </option>
              </select>
            </div>
          </div>

          <div class="row mt-2">
            <country-selector :country="item.country" @countrySelected="clearAddress($event)"></country-selector>
          </div>

          <div class="row mt-2" v-if="item.country === australia">
            <autocomplete-address
              ref="autocomplete-address"
              @addressSelected="getSupporterSelectedAddress"
            ></autocomplete-address>
          </div>

          <div class="row mt-2">
            <div class="field">
              <input
                v-model="item.unitNumber"
                type="text"
                placeholder="Unit number"
                autocomplete="nope"
                maxlength="31"
              />
            </div>

            <div class="field">
              <input
                required
                v-model="item.streetNumber"
                type="text"
                placeholder="Street number"
                autocomplete="nope"
                maxlength="31"
              />
            </div>
          </div>

          <div class="row mt-2">
            <div class="field">
              <input
                required
                v-model="item.streetName"
                type="text"
                placeholder="Street name"
                autocomplete="nope"
                maxlength="127"
              />
            </div>

            <div class="field">
              <input
                required
                v-model="item.suburb"
                type="text"
                placeholder="Suburb"
                autocomplete="nope"
                maxlength="63"
              />
            </div>
          </div>

          <div class="row mt-2">
            <div class="field">
              <input
                required
                v-model="item.postalCode"
                type="text"
                placeholder="Postcode"
                autocomplete="nope"
                maxlength="15"
              />
            </div>
            <div class="field">
              <select required name="state" v-if="item.country === australia" v-model="item.state">
                <option value disabled selected>Select a state</option>
                <option value="NSW">NSW</option>
                <option value="ACT">ACT</option>
                <option value="NT">NT</option>
                <option value="SA">SA</option>
                <option value="WA">WA</option>
                <option value="VIC">VIC</option>
                <option value="QLD">QLD</option>
                <option value="TAS">TAS</option>
              </select>
              <input
                v-if="item.country !== australia"
                v-model="item.state"
                type="text"
                placeholder="State"
                autocomplete="nope"
                maxlength="63"
              />
            </div>
          </div>
          <!-- STATUS -->
          <h3 class="sub-title">Status</h3>
          <div class="row mt-2">
            <div class="field">
              <Toggle ref="togglePrimary" label="Primary" :default="false"></Toggle>
            </div>
            <div class="field">
              <Toggle ref="toggleDoNotMail" label="Do Not Mail" :default="false"></Toggle>
            </div>
          </div>
        </form>
      </div>

      <!-- Error Messages -->
      <Alert v-if="errorMessage" :message="errorMessage"></Alert>

      <div class="footer">
        <button class="secondary" type="button" @click="close()">Cancel</button>
        <div class="flex-grow"></div>
        <button type="button" @click="add()" :disabled="!canSubmit" select-cy="submit-add-supporter">
          <span v-if="!isValidating">Save</span>
          <span class="icon" v-if="isValidating">
            <i class="icn icn-refresh sm animate-reverse-spin"></i>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ALERT_TYPE, MESSAGES, ADDRESS_LABEL, CONTACT_TYPE } from '@/helpers/constants';
import { MODAL_NAME } from '../../stores/supporters';
import AutocompleteAddress from '@/components/AutocompleteAddress.vue';
import Alert from '@/components/Alert.vue';
import Toggle from '@/components/Toggle';
import CountrySelector from '@/components/CountrySelector.vue';

export default {
  name: 'SupporterEditAddress',
  components: {
    Alert,
    AutocompleteAddress,
    CountrySelector,
    Toggle,
  },

  data() {
    return {
      australia: 'Australia',
      item: {
        label: null,
        country: 'Australia',
        streetNumber: null,
        streetName: null,
        suburb: null,
        postalCode: null,
        state: null,
        unitNumber: null,
        doNotMail: undefined,
        isPrimary: undefined,
      },
      isValidating: false,
      isProcessing: false,
      message: null,
    };
  },
  computed: {
    canSubmit() {
      return !this.isProcessing;
    },
    labels() {
      if (this.item) {
        return Object.values(ADDRESS_LABEL['ADDRESS']);
      }
      return null;
    },
    errorMessage: {
      get() {
        return this.message;
      },
      set(value) {
        if (value === null) {
          this.message = null;
        } else {
          this.message = {
            content: value,
            type: ALERT_TYPE.ERROR,
          };
        }
      },
    },
  },

  props: {
    supporter: Object,
  },

  methods: {
    close() {
      // this.$store.dispatch('supporters/get', {
      //   ids: this.item.id,
      // });
      this.$store.commit('supporters/close', MODAL_NAME.ADD_ADDRESS);
    },
    async add() {
      if (this.isValidating) return;
      this.errorMessage = null;
      this.isValidating = true;
      const isValid = document.querySelector('#address-add').reportValidity();
      if (isValid) {
        this.item.isPrimary = this.$refs.togglePrimary.get();
        this.item.doNotMail = this.$refs.toggleDoNotMail.get();

        const payload = {
          supporterId: this.supporter.id,
          addresses: [this.item],
        };
        console.log(payload);

        // Create update details for WWF people to update to RE
        if (this.supporter.reConstituentId) {
          this.createUpdateDetails(payload);
        }

        // Add address to Pandata
        this.$store
          .dispatch('supporters/createUpdateAddress', [payload])
          .then(() => {
            this.$store.commit('supporters/refresh');
            this.close();
          })
          .catch(err => {
            console.error(err);
            if (err && err.status && err.status === 401) {
              this.errorMessage = MESSAGES.NO_PERMISSION;
            } else {
              this.errorMessage = MESSAGES.SERVER_ERROR;
            }
          })
          .finally(() => (this.isValidating = false));
      } else {
        this.isValidating = false;
      }
    },
    removeValidity() {
      document.querySelector('#address').setCustomValidity('');
    },
    getSupporterSelectedAddress({ address, fullLine }) {
      this.item = { ...this.item, ...address };
      this.item.unitNumber = address.unitNumber && address.unitNumber.trim() !== '' ? address.unitNumber.trim() : null;
      this.item.addressFullLine = `${fullLine}, ${this.item.country}`;
    },
    clearAddress(event) {
      this.item.country = event.country;
      this.item.addressFullLine = null;
      this.item.streetNumber = null;
      this.item.streetName = null;
      this.item.suburb = null;
      this.item.postalCode = null;
      this.item.state = null;
      this.item.unitNumber = null;
    },
    createUpdateDetails(data) {
      const payload = {
        ...data,
        email: this.supporter.contacts
          .filter(contact => contact.type === CONTACT_TYPE.EMAIL)
          .sort((a, b) => (b.isPrimary ? 1 : -1))[0]?.contact,
      };
      payload.reConstituentId = this.supporter.reConstituentId;
      payload.createdAt = new Date();

      this.$store
        .dispatch('supporters/createUpdateDetails', [payload])
        .then(() => {
          this.$store.dispatch('notifications/success', {
            headline: 'Contact Details Updated',
            details: 'The request has been submitted.',
          });
          this.close();
        })
        .catch(err => {
          console.error(err);
          if (err && err.status && err.status === 401) {
            this.errorMessage = MESSAGES.NO_PERMISSION;
          } else {
            this.errorMessage = MESSAGES.SERVER_ERROR;
          }
        })
        .finally(() => (this.isValidating = false));
    },
  },
};
</script>
